/**
 * Authenticated API client.
 * Wraps fetch() to send the httpOnly app-session cookie (#469) with every API
 * request. No per-request Entra token: the session is established once via
 * POST /api/auth/entra/callback (see services/sessionClient.ts). MSAL tokens
 * are only acquired for direct Microsoft Graph/SharePoint calls.
 */

import { apiConfig } from '../config/apiConfig';
import { authStore } from '../stores/auth';

/**
 * Drop the local session and bounce to /login. Never redirects to Microsoft —
 * re-auth always starts with an explicit click on the login page (#469).
 */
function redirectToLogin(): void {
  authStore.clearAuth();
  if (window.location.pathname === '/login') return;
  try {
    // Remember where the user was so the post-login redirect can return them.
    sessionStorage.setItem('authRedirectPath', window.location.pathname + window.location.search);
    sessionStorage.setItem('survey-auth-redirect-recovery', 'true');
  } catch { /* sessionStorage unavailable — non-critical */ }
  window.location.assign('/login');
}

/**
 * Make an authenticated fetch request.
 * Sends the session cookie via credentials: 'include'. A 401 means the
 * session is missing or expired, so the user is sent back to /login.
 *
 * Under VITE_DEV_AUTH_BYPASS, sends the same X-Dev-User-Id header the portal
 * client sends, so a locally-running site API with its dev session bypass can
 * authenticate browser-driven local tests. Inert in production builds.
 */
const DEV_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

export async function apiFetch(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const headers = new Headers(options.headers || {});
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (DEV_BYPASS) {
    headers.set('X-Dev-User-Id', 'dev-admin');
  }

  const url = path.startsWith('http') ? path : `${apiConfig.baseURL}${path}`;

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (response.status === 401) {
    redirectToLogin();
  }

  return response;
}
